import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  IAdditionalCharge,
  IAdditionalChargeStatus,
  IApproveAdditionalCharge,
  ICustomerAdditionalCharge,
  IExpenseAssigneeOptionsResult,
  ISupplierInformationAdditionalCharge,
} from '../interfaces/additional-charge';
import { IAdditionalChargeFilter } from '../interfaces/additional-charge-filter';
import { EditService } from './edit.service';

import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { AdditionalCostRates } from '../interfaces/additional-cost-rates';

import {
  InternalRequestVMEAdditionalChargeDTO,
  VMEAdditionalChargeJobScreenResult,
} from '../interfaces/screen-specific/job-screen';
import { IPageResult } from '../interfaces/page-result';
import { SupplierAdditionalChargeJobScreenResult } from '../interfaces/screen-specific/job-screen/supplier/supplier-job-screen-additional-charge';
import { AdditionalChargeStatusEnum } from '../enums/additional-charge.enum';
import { IPendingExpenseFilter } from '../interfaces/inbox-job-filter';
import { IPendingExpensesInboxResult } from '../interfaces/pending-expense-inbox-result';
import moment from 'moment/moment';
import { Message } from '../classes/Message';
import FileSaver from 'file-saver';
import { MessageService } from 'primeng';

@Injectable({ providedIn: 'root' })
export class AdditionalChargeService extends EditService<
  IAdditionalCharge,
  IAdditionalChargeFilter
> {
  url = '/api/additionalcharge';
  entityTitle = 'AdditionalCharge';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  override getPage(
    filterCommand: IAdditionalChargeFilter,
  ): Promise<IPageResult<IAdditionalCharge>> {
    throw new Error('Dedicated endpoints should be used instead of this generic method');
  }

  override get(id: number | string): Promise<IAdditionalCharge> {
    return this.handleRequest<IAdditionalCharge>(
      this.http.get<IAdditionalCharge>(`${this.url}/${id}`),
      this.handlersFactory.getForReadById(this.entityTitle),
    );
  }

  save(data: any): Promise<any> {
    return this.http
      .post(`${this.url}` + '/save', data)
      .toPromise()
      .catch(this.handleError);
  }

  sendIternalRequest(data: InternalRequestVMEAdditionalChargeDTO): Promise<any> {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}` + '/internal-request', data),
      this.handlersFactory.getForCreate('Internal Request has been raised'),
    );
  }

  getAdditionalChargeTypes(): Promise<any[]> {
    return this.http
      .get<any>(`${this.url}` + '/getAdditionalChargeTypes')
      .toPromise()
      .catch(this.handleError);
  }

  getAdditionalChargeStatuses(): Promise<IAdditionalChargeStatus[]> {
    return this.http
      .get<any>(`${this.url}` + '/getAdditionalChargeStatuses')
      .toPromise()
      .catch(this.handleError);
  }

  getTaskNumbersByJob(id: number): Promise<any[]> {
    return this.http
      .get<any[]>(`${this.url}` + '/getTaskNumbersByJob/' + id.toString())
      .toPromise()
      .catch(this.handleError);
  }

  approveAdditionalCharge(data: IApproveAdditionalCharge): Promise<any> {
    return this.http
      .post<any>(`${this.url}/approveAdditionalCharge`, data)
      .toPromise()
      .catch(this.handleError);
  }

  deleteAdditionalCharge(data: any): Promise<any> {
    return this.http
      .post<any>(`${this.url}` + '/deleteAdditionalCharge', data)
      .toPromise()
      .catch(this.handleError);
  }

  getCustomerChargesByJob(jobId: number): Promise<ICustomerAdditionalCharge[]> {
    return this.http
      .get<any[]>(`${this.url}/${jobId}/customer-charges`)
      .toPromise()
      .catch(this.handleError);
  }

  getVmeAdditionalCostsForJob(jobId: number): Promise<VMEAdditionalChargeJobScreenResult> {
    return this.http
      .get<VMEAdditionalChargeJobScreenResult>(`${this.url}/for-job-vme/${jobId}`)
      .toPromise()
      .catch(this.handleError);
  }

  getSupplierAdditionalCostsForJob(
    jobId: number,
  ): Promise<SupplierAdditionalChargeJobScreenResult> {
    return this.http
      .get<SupplierAdditionalChargeJobScreenResult>(`${this.url}/for-job-supplier/${jobId}`)
      .toPromise()
      .catch(this.handleError);
  }

  declineAdditionalCharge(data: any): Promise<any> {
    return this.http
      .post<any>(`${this.url}` + '/declineAdditionalCharge', data)
      .toPromise()
      .catch(this.handleError);
  }

  autoValidate(id: number): Promise<any> {
    return this.http
      .post<any>(`${this.url}` + '/autoValidate/' + id.toString(), null)
      .toPromise()
      .catch(this.handleError);
  }

  getAdditionalCostRates(taskId): Promise<AdditionalCostRates> {
    return this.http
      .get<AdditionalCostRates>(`${this.url}/costRates/${taskId}`)
      .toPromise()
      .catch(this.handleError);
  }

  sendRequestInfoEmail(id, emailDTO) {
    return this.handleRequest<boolean>(
      this.http.post<boolean>(
        `${this.url}` + '/' + id.toString() + '/sendRequestInfoEmail',
        emailDTO,
      ),
      this.handlersFactory.getForCreate('Request for Information '),
    );
  }

  getRequestInfoMail(id, choiceId: number): Promise<any> {
    return this.http
      .get<any>(
        `${this.url}` + '/' + id.toString() + '/populateRequestInfoTemplate/' + choiceId.toString(),
      )
      .toPromise()
      .catch(this.handleError);
  }

  RequestInfoChoiceSearch() {
    return this.http.get<any>(`${this.url}` + '/additionalChargeQuestions').toPromise();
  }

  deleteAdditionalInfo(id) {
    return this.http
      .delete<any>(`${this.url}` + '/additionalChargeQuestion/' + id)
      .toPromise()
      .catch(this.handleError);
  }

  patchAdditionalInfo(id, patch: any) {
    return this.http
      .patch<any>(`${this.url}` + '/additionalChargeQuestion/' + id, patch)
      .toPromise()
      .catch(this.handleError);
  }

  createAdditionalInfo(dto) {
    return this.http
      .post<any>(`${this.url}` + '/additionalChargeQuestion', dto)
      .toPromise()
      .catch(this.handleError);
  }

  checkIfTaskHasExceededExpenseUploadThreshold(taskId: number): Promise<boolean> {
    return this.http
      .get<boolean>(
        `${this.url}/checkIfTaskHasExceededExpenseUploadThreshold/${encodeURIComponent(taskId)}`,
      )
      .toPromise()
      .catch(this.handleError);
  }

  syncExpenseTransactionLines(jobId: number) {
    return this.http
      .post(`${this.url}/${jobId}/sync-transaction-lines`, {})
      .toPromise()
      .catch(this.handleError);
  }

  getSupplierAdditionalCosts(
    filterCommand: IAdditionalChargeFilter,
  ): Promise<IPageResult<IAdditionalCharge>> {
    return this.handleRequest<IPageResult<IAdditionalCharge>>(
      this.http.get<IPageResult<IAdditionalCharge>>(this.url, {
        params: this.constructHttpParams(filterCommand),
      }),
      this.handlersFactory.getForReadByFilter(),
    );
  }

  getSupplierInformationAdditionalCosts(
    filterCommand: IAdditionalChargeFilter,
  ): Promise<IPageResult<ISupplierInformationAdditionalCharge>> {
    const url = '/api/supplierInformationAdditionalCosts';
    return this.handleRequest<IPageResult<ISupplierInformationAdditionalCharge>>(
      this.http.get<IPageResult<ISupplierInformationAdditionalCharge>>(url, {
        params: this.constructHttpParams(filterCommand),
      }),
      this.handlersFactory.getForReadByFilter(),
    );
  }

  changeAssignee(
    additionalChargeToTaskId: number,
    userId: string,
    jobId: number,
    assignOtherPendingExpenses: boolean,
  ): Promise<void> {
    return this.handleRequest<any>(
      this.http.post<any>(`${this.url}` + `/${additionalChargeToTaskId}/change-assignee`, {
        userId,
        assignOtherPendingExpenses,
        jobId,
      }),
      this.handlersFactory.getForCreate('Change assignee successful'),
    );
  }

  changeExpenseStatus(
    additionalChargeToTaskId: number,
    additionalChargeStatusId: number,
  ): Promise<void> {
    return this.handleRequest<any>(
      this.http.put<any>(
        `${this.url}` + `/change-additional-charge-status/${additionalChargeToTaskId}`,
        {
          additionalChargeStatusId: additionalChargeStatusId,
        },
      ),
      this.handlersFactory.getForCreate('Change status successful'),
    );
  }

  getPendingExpensesInbox(filterCommand: IPendingExpenseFilter) {
    return this.http
      .get<IPendingExpensesInboxResult>(`${this.url}/pending-expenses-inbox`, {
        params: this.constructHttpParams(filterCommand),
      })
      .toPromise();
  }

  getExpenseAssigneeOptions() {
    return this.http
      .get<IExpenseAssigneeOptionsResult>(`${this.url}/expense-assignee-options`)
      .toPromise();
  }

  downloadPendingExpensesInboxCSV(filterCommand: IPendingExpenseFilter) {
    let filename = `Inbox_AdditionalCostsRequests_${moment().format('YYYYMMMMDo_h:mm')}.csv`;

    let params = this.constructHttpParams(filterCommand);

    let report = this.http.get(`${this.url}/pending-expenses-inbox-csv`, {
      params: params,
      responseType: 'blob',
    });
    report.subscribe(
      (x) => this.handleDownloadFile(x, filename),
      (error) => {
        this.messageService.add(Message.Error('Impossible to download the csv.'));
      },
      () => {
        this.messageService.add(
          Message.Success('Downloaded successful. Check your download folder.'),
        );
      },
    );

    return report;
  }

  private handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }
}
